/**
 * Workflow State Hooks
 * 
 * Bridge hooks that connect enhanced components to the new WorkflowStateProvider
 * Provides backward compatibility while enabling new state management features
 */

import { useContext, useCallback } from 'react';
import { WorkflowContext, type WorkflowContextValue } from '../state/WorkflowStateProvider';

// Mock types for now - would be imported from @ferp/api-client when available
type WorkflowDefinition = any;
type WorkflowInstance = any;
type Task = any;
type Signal = any;
type Policy = any;

// Custom hook to use workflow context
export const useWorkflowState = (): WorkflowContextValue => {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflowState must be used within WorkflowStateProvider');
  }
  return context;
};

// Hook for workflow definitions
export const useWorkflowDefinitions = () => {
  const { state, definitions } = useWorkflowState();
  
  return {
    items: state.definitions.entities,
    loading: state.definitions.loading.list,
    error: state.definitions.error,
    refetch: useCallback(() => definitions.fetch(), [definitions]),
    create: useCallback((data: Partial<WorkflowDefinition>) => definitions.create(data), [definitions]),
    update: useCallback((id: string, data: Partial<WorkflowDefinition>) => definitions.update(id, data), [definitions]),
    delete: useCallback((id: string) => definitions.delete(id), [definitions])
  };
};

// Hook for workflow instances
export const useWorkflowInstances = () => {
  const { state, instances } = useWorkflowState();
  
  return {
    items: state.instances.entities,
    loading: state.instances.loading.list,
    error: state.instances.error,
    refetch: useCallback(() => instances.fetch(), [instances]),
    create: useCallback((data: Partial<WorkflowInstance>) => instances.create(data), [instances]),
    update: useCallback((id: string, data: Partial<WorkflowInstance>) => instances.update(id, data), [instances]),
    delete: useCallback((id: string) => instances.delete(id), [instances])
  };
};

// Hook for tasks
export const useTasks = () => {
  const { state, tasks } = useWorkflowState();
  
  return {
    tasks: Object.values(state.tasks.entities),
    loading: state.tasks.loading.list,
    error: state.tasks.error,
    refetch: useCallback(() => tasks.fetch(), [tasks]),
    create: useCallback((data: Partial<Task>) => tasks.create(data), [tasks]),
    update: useCallback((id: string, data: Partial<Task>) => tasks.update(id, data), [tasks]),
    delete: useCallback((id: string) => tasks.delete(id), [tasks])
  };
};

// Hook for signals
export const useSignals = () => {
  const { state, signals } = useWorkflowState();
  
  return {
    items: state.signals.entities,
    loading: state.signals.loading.list,
    error: state.signals.error,
    refetch: useCallback(() => signals.fetch(), [signals]),
    create: useCallback((data: Partial<Signal>) => signals.create(data), [signals]),
    update: useCallback((id: string, data: Partial<Signal>) => signals.update(id, data), [signals]),
    delete: useCallback((id: string) => signals.delete(id), [signals]),
    // Additional properties for compatibility
    signals: state.signals.entities
  };
};

// Hook for policies
export const usePolicies = () => {
  const { state, policies } = useWorkflowState();
  
  return {
    items: state.policies.entities,
    loading: state.policies.loading.list,
    error: state.policies.error,
    refetch: useCallback(() => policies.fetch(), [policies]),
    create: useCallback((data: Partial<Policy>) => policies.create(data), [policies]),
    update: useCallback((id: string, data: Partial<Policy>) => policies.update(id, data), [policies]),
    delete: useCallback((id: string) => policies.delete(id), [policies])
  };
};

// Hook for task mutations (backward compatibility)
export const useTaskMutations = () => {
  const { tasks } = useWorkflowState();
  
  return {
    createTask: useCallback((data: Partial<Task>) => tasks.create(data), [tasks]),
    updateTask: useCallback((id: string, data: Partial<Task>) => tasks.update(id, data), [tasks]),
    deleteTask: useCallback((id: string) => tasks.delete(id), [tasks]),
    loading: false // Will be managed by state
  };
};

// Hook for workflow definition mutations (backward compatibility)
export const useWorkflowDefinitionMutation = () => {
  const { definitions } = useWorkflowState();
  
  return {
    save: useCallback((data: Partial<WorkflowDefinition>) => definitions.create(data), [definitions]),
    update: useCallback((id: string, data: Partial<WorkflowDefinition>) => definitions.update(id, data), [definitions]),
    loading: false // Will be managed by state
  };
};

// Hook for instances (alias for backward compatibility)
export const useInstances = () => {
  const result = useWorkflowInstances();
  return {
    ...result,
    instances: result.items
  };
};

// Hook for instance mutations (backward compatibility)
export const useInstanceMutation = () => {
  const { instances } = useWorkflowState();
  
  return {
    save: useCallback((data: Partial<WorkflowInstance>) => instances.create(data), [instances]),
    create: useCallback((data: Partial<WorkflowInstance>) => instances.create(data), [instances]),
    update: useCallback((id: string, data: Partial<WorkflowInstance>) => instances.update(id, data), [instances]),
    delete: useCallback((id: string) => instances.delete(id), [instances]),
    loading: false // Will be managed by state
  };
};

// Hook for UI state management
export const useWorkflowUI = () => {
  const { state, ui } = useWorkflowState();
  
  return {
    sidebarCollapsed: state.ui.sidebarCollapsed,
    theme: state.ui.theme,
    currentView: state.ui.currentView,
    breadcrumbs: state.ui.breadcrumbs,
    modals: state.ui.modals,
    notifications: state.ui.notifications,
    setSidebarCollapsed: ui.setSidebarCollapsed,
    setTheme: ui.setTheme,
    setCurrentView: ui.setCurrentView,
    setBreadcrumbs: ui.setBreadcrumbs,
    addModal: ui.addModal,
    removeModal: ui.removeModal,
    addNotification: ui.addNotification,
    removeNotification: ui.removeNotification
  };
};

// Hook for real-time features
export const useWorkflowRealtime = () => {
  const { state, realtime } = useWorkflowState();
  
  return {
    connected: state.realtime.connected,
    subscriptions: state.realtime.subscriptions,
    updates: state.realtime.updates,
    subscribe: realtime.subscribe,
    unsubscribe: realtime.unsubscribe,
    handleUpdate: realtime.handleUpdate
  };
};

// Hook for offline sync features
export const useWorkflowSync = () => {
  const { state, sync } = useWorkflowState();
  
  return {
    online: state.sync.online,
    syncing: state.sync.syncing,
    pendingOperations: state.sync.pendingOperations,
    conflictResolution: state.sync.conflictResolution,
    setOnline: sync.setOnline,
    addPendingOperation: sync.addPendingOperation,
    removePendingOperation: sync.removePendingOperation,
    resolveConflict: sync.resolveConflict
  };
};

// Additional backward compatibility hooks for data/hooks pattern

// Job Status hooks
export const useJobs = () => usePolicies(); // Mock implementation
export const useJobMetrics = () => ({ 
  metrics: {
    avgResourceUsage: {
      cpu: 0,
      memory: 0,
      disk: 0,
      network: 0
    },
    totalJobs: 0,
    completedJobs: 0,
    failedJobs: 0,
    averageExecutionTime: 0
  }, 
  loading: false 
});
export const useJobMutations = () => {
  const { createTask, updateTask, deleteTask, loading } = useTaskMutations();
  
  return {
    createTask,
    updateTask,
    deleteTask,
    loading,
    // Job-specific methods mapped to task operations
    cancelJob: useCallback(async (jobId: string) => {
      return await updateTask(jobId, { status: 'cancelled' });
    }, [updateTask]),
    retryJob: useCallback(async (jobId: string) => {
      return await updateTask(jobId, { status: 'pending', retryCount: Date.now() });
    }, [updateTask]),
    deleteJob: useCallback(async (jobId: string) => {
      return await deleteTask(jobId);
    }, [deleteTask]),
    updateProgress: useCallback(async (jobId: string, progress: number) => {
      return await updateTask(jobId, { progress });
    }, [updateTask]),
    createJob: useCallback(async (data: any) => {
      return await createTask(data);
    }, [createTask]),
    // Additional methods for compatibility
    save: useCallback(async (data: any) => {
      return await createTask(data);
    }, [createTask])
  };
};
export const useJobQueue = () => ({ queue: [], loading: false });
export const useJob = (id: string) => ({ 
  job: null, 
  loading: false,
  isActive: false,
  refetch: async () => {}
});
export const useJobLogs = (id: string) => ({ 
  logs: [], 
  loading: false,
  refetch: async () => {}
});

// Signal hooks
export const useSignalMutations = () => ({
  create: useCallback((data: Partial<Signal>) => Promise.resolve(data), []),
  update: useCallback((id: string, data: Partial<Signal>) => Promise.resolve(data), []),
  delete: useCallback((id: string) => Promise.resolve(), []),
  loading: false,
  // Additional methods for compatibility
  sendSignal: useCallback((id: string, data: any) => Promise.resolve(data), []),
  updateSignal: useCallback((id: string, data: any) => Promise.resolve(data), []),
  deleteSignal: useCallback((id: string) => Promise.resolve(), [])
});

// Context hooks
export const useContextMutations = () => {
  const base = useTaskMutations();
  return {
    ...base,
    // Additional methods for compatibility
    createContext: base.createTask,
    updateContext: base.updateTask
  };
};

// Escalation hooks  
export const useEscalationMutations = () => {
  const base = useTaskMutations();
  return {
    ...base,
    createEscalation: base.createTask,
    updateEscalation: base.updateTask
  };
};

// Integration hooks
export const useIntegrationMutations = () => {
  const base = useTaskMutations();
  return {
    ...base,
    createIntegration: base.createTask,
    updateIntegration: base.updateTask,
    testIntegration: useCallback(async (id: string) => {
      return { success: true, message: 'Test successful' };
    }, [])
  };
};

// Schedule hooks
export const useSchedules = () => usePolicies(); // Mock implementation
export const useScheduleMutation = () => useTaskMutations();

// Timeline hooks
export const useTimelineEvents = () => ({ 
  events: [], 
  loading: false,
  error: { hasError: false, message: null },
  refetch: async () => {}
});
export const useTimelineAnalytics = () => ({ analytics: {}, loading: false });